import { env } from './env'
import { jurisdictions, type GesetzeFindenSite } from './jurisdiction.constants'
import type { Source } from './norms'

export type Jurisdiction = (typeof jurisdictions)[number]

export interface JurisdictionInput {
  normId?: string
  source?: Source
  host?: string | null
}

export const jurisdiction = env.NEXT_PUBLIC_DEFAULT_JURISDICTION ?? 'at'
export const isDeJurisdiction = jurisdiction === 'de'
export const appName = isDeJurisdiction
  ? 'GesetzeFinden.de'
  : 'GesetzeFinden.at'
export const supportEmail = isDeJurisdiction
  ? 'hallo@gesetzefinden.at'
  : 'hallo@gesetzefinden.at' // no de email yet

export type JurisdictionFilter = Jurisdiction | readonly Jurisdiction[]

/**
 * What a GesetzeFinden site publishes. Only jurisdictions with a lawType table
 * in `paths.ts` belong here — `getUrl()` throws NO_MAPPING on any other hit.
 */
export function jurisdictionFilterFor(site: GesetzeFindenSite): Jurisdiction[] {
  return [site, 'eu']
}

/** {@link jurisdictionFilterFor} for the jurisdiction this deployment serves. */
export const jurisdictionFilter: JurisdictionFilter =
  jurisdictionFilterFor(jurisdiction)

/**
 * Infer jurisdiction from normId/source prefixes.
 * - eu.* => eu
 * - de.* or source de.ris => de
 * - ro.* or source ro.legislatie => ro
 * - es.* or source es.boe => es
 * - fallback at
 */
export function inferJurisdiction({
  normId,
  source,
}: JurisdictionInput): Jurisdiction {
  if (normId?.startsWith('eu.')) return 'eu'
  if (normId?.startsWith('de.') || source === 'de.ris') return 'de'
  if (normId?.startsWith('ro.') || source === 'ro.legislatie') return 'ro'
  if (normId?.startsWith('es.') || source === 'es.boe') return 'es'
  return 'at'
}

/** `filter_by` fragment for the `documents` and `citations` collections. */
export function typesenseJurisdictionFilter(
  filter: JurisdictionFilter,
): string {
  const included = Array.isArray(filter) ? filter : [filter]
  const conditions = included.map((code) => `jurisdiction:=${code}`)
  // ~5.6k documents predate the `jurisdiction` field and hold no value; they
  // are AT laws (as `sqlJurisdictionFilter` treats NULL), and only `!=` matches them.
  if (included.includes('at')) {
    conditions.push(`jurisdiction:!=[${jurisdictions.join(',')}]`)
  }
  return `(${conditions.join(' || ')})`
}
